// @flow
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from 'urql';
import { featureIsEnabled } from '@ta/common.features';
import { RouteContext } from '@ta/common.routing';
import { Device, Visitor } from '@ta/lithium.context';
import { useQuestionFlow } from '@ta/onboarding.common';
import InitialExplicitPreferencesQuery, {
  type InitialExplicitPreferencesQuery as ExplicitPreferencesData,
  type ExplicitPreferences_CompanionType as SegmentOption,
  type InitialExplicitPreferencesQuery_explicitPreferences_interests_value as Interest,
} from './InitialExplicitPreferencesQuery.gql';
import ExplicitPreferencesQuery from './ExplicitPreferencesQuery.gql';
import ExplicitPreferencesMutation from './ExplicitPreferencesMutation.gql';
import ProfileMutation from './ProfileMutation.gql';

export type Step =
  | 'HAS_DESTINATION'
  | 'DESTINATION'
  | 'DATES'
  | 'SEGMENT'
  | 'INTERESTS'
  | 'PROFILE'
  | 'END_SCREEN';

export type Responses = {|
  hasDestination: ?boolean,
  isInDestination: ?boolean,
  destinationGeoId: ?number,
  flexibleDates: boolean,
  startMonth: ?Date,
  endMonth: ?Date,
  startDate: ?Date,
  endDate: ?Date,
  segment: ?SegmentOption,
  interests: string[],
  firstName: string,
  homeGeoId: ?number,
|};

const parseDate = (dateStr: ?string) => {
  const match = (dateStr || '').match(/([0-9]{4})-([0-9]{2})-([0-9]{2})/);
  if (match == null) {
    return null;
  }

  return new Date(+match[1], +match[2] - 1, +match[3]);
};

const formatDate = (date: ?Date) => {
  if (date == null) {
    return null;
  }

  return date.toISOString().substr(0, 10);
};

/**
 * Format an interest into a single string identifier, which is used for HTML input values on the front end
 */
const getInterestId = (interestObject: ?Interest) =>
  `${interestObject?.type || ''}|${interestObject?.value || ''}`;

/**
 * Parse an interest identifier into the object as stored by the insights service
 */
const parseInterestId = (interestStr: string) => {
  const [type, value] = interestStr.split('|');
  return { type, value };
};

/**
 * @return response state given the model from the Explicit Preferences service
 */
const getResponses = (data: ?$Shape<ExplicitPreferencesData>): $Shape<Responses> => {
  const responses = {};

  const startDate = parseDate(data?.explicitPreferences?.dates?.value?.startDate);
  const endDate = parseDate(data?.explicitPreferences?.dates?.value?.endDate);
  const flexibleDates = data?.explicitPreferences?.dates?.value?.flexibleDates;
  const segment = data?.explicitPreferences?.segment?.value;
  const interests = data?.explicitPreferences?.interests?.value;
  const firstName = data?.memberProfile?.displayName;
  const homeGeoId = data?.memberProfile?.hometown?.location?.locationId;

  if (startDate != null) {
    if (flexibleDates) {
      responses.startMonth = startDate;
    } else {
      responses.startDate = startDate;
    }
  }

  if (endDate != null) {
    if (flexibleDates) {
      responses.endMonth = endDate;
    } else {
      responses.endDate = endDate;
    }
  }

  if (startDate != null && endDate != null && flexibleDates != null) {
    responses.flexibleDates = flexibleDates;
  }

  if (segment != null) {
    responses.segment = segment;
  }

  if (firstName != null) {
    responses.firstName = firstName;
  }

  if (homeGeoId != null) {
    responses.homeGeoId = homeGeoId;
  }

  const selectedInterests = (interests || [])
    .filter(interest => interest?.selected)
    .map(getInterestId);

  if (selectedInterests.length > 0) {
    responses.interests = selectedInterests;
  }

  return responses;
};

/**
 * Wraps around a location ID and also keeps track of the associated location name. The initial name, if available,
 * usually comes from GraphQL, and updates could come from a typeahead input
 */
const useLocationIdAndName = (
  locationId: ?number,
  initialLocationName: ?string,
  setLocationId: (?number) => mixed,
) => {
  const [locationName, setLocationName] = useState(initialLocationName);

  const setLocationIdAndName = useCallback(
    (newLocationId: ?number, newLocationName: ?string) => {
      setLocationId(newLocationId);
      setLocationName(newLocationName);
    },
    [setLocationId],
  );

  useEffect(() => {
    setLocationName(initialLocationName);
  }, [initialLocationName]);

  return [locationName, setLocationIdAndName];
};

export type ExplicitPreferencesMode =
  /**
   * The onboarding flow started immediately after normal registration. For this case, we need to include
   * destination and profile setup questions. Show different stuff depending on which servlet we come from
   */
  | 'ONBOARDING'

  /**
   * The flow was started either by the user choosing to edit their trip on scoped /Home, or a CRM
   * email deep linking to the flow. Either way, we won't ask for destination or profile info.
   */
  | 'EDITING'

  /**
   * The flow started immediately after registration when coming from google one-tap.
   * Based on tests we show different things
   */
  | 'ONETAP'

  /**
   * The flow started immediately after selecting a geo from the geopill
   */
  | 'GEOPILL';

type ExplicitPreferencesOptions = {|
  /**
   * Which version of the flow to use
   */
  mode: ExplicitPreferencesMode,

  /**
   * The current scoped geo, if any. This is used for the default destination.
   */
  scopedGeoId: ?number,

  /**
   * If true, don't do any GraphQL queries yet. This is set if the flow is disabled to avoid
   * requested unnecessary data.
   */
  pause: boolean,
|};

/**
 * The state machine for Explicit Preferences steps. This contains all of the logic for fetching the
 * existing data, managing the state of the question flow, and submitting changes. The UI of each
 * step is rendered by {@link ./ExplicitPreferencesUI}.
 *
 * @param mode - which version of the flow to use
 * @param scopedGeoId - the scoped geo, if any. This is used for the default destination.
 * @param pause - if true, don't do any GraphQL queries yet
 */
const useExplicitPreferencesState = ({ mode, scopedGeoId, pause }: ExplicitPreferencesOptions) => {
  const { userId } = useContext(Visitor);

  /**
   * Initial data that we won't refetch during the flow. This includes the member profile, the
   * current scoped geo, and previous personalization responses for the current scoped geo.
   */
  const [{ data: initialExplicitPreferencesData }] = useQuery({
    query: InitialExplicitPreferencesQuery,
    variables: {
      userId,
      // LSS requires an array of locationIds, explicit-preferences takes one optional locationId
      destinationGeoIds: scopedGeoId != null ? [scopedGeoId] : [],
      destinationGeoId: scopedGeoId,
    },
    pause,
  });

  const initialHomeGeoName =
    initialExplicitPreferencesData?.memberProfile?.hometown?.location?.name;
  const initialDestinationGeoName = initialExplicitPreferencesData?.locations?.[0]?.name;

  // As a special case, we can skip the interests screen if there are no interests for the selected
  // geo.
  const [hasInterestOptions, setHasInterestOptions] = useState(false);

  const {
    route: { page },
  } = useContext(RouteContext);

  // The actual questions. The order depends on the test variant, and whether or not the current
  // geo has any interest options.
  const questionSteps = useMemo(() => {
    let steps;

    if (
      mode === 'ONBOARDING' ||
      mode === 'GEOPILL' ||
      mode === 'ONETAP' ||
      featureIsEnabled('t11209_mip_variant_b') ||
      featureIsEnabled('t11208_variant_a_curated_broad_interest_categories')
    ) {
      steps = ['DATES', 'SEGMENT', 'INTERESTS'];
    } else if (featureIsEnabled('t11210_mip_variant_c')) {
      steps = ['SEGMENT', 'DATES', 'INTERESTS'];
    } else {
      steps = ['INTERESTS', 'SEGMENT', 'DATES'];
    }

    // Skip the interests step if there are no interests for this geo
    if (!hasInterestOptions) {
      steps.splice(steps.indexOf('INTERESTS'), 1);
    }

    return steps;
  }, [hasInterestOptions, mode]);

  const getNextStep = useCallback(
    (step, { hasDestination, destinationGeoId }): ?Step => {
      switch (step) {
        case 'PROFILE':
          if (
            (featureIsEnabled('t11213_mip_2_account_screen') && mode === 'ONETAP') ||
            (featureIsEnabled('t11216_mip_2_traditional_sign_up_ll') &&
              mode === 'ONBOARDING' &&
              page !== 'Home')
          ) {
            return 'END_SCREEN';
          }
          return 'HAS_DESTINATION';

        case 'HAS_DESTINATION':
          if (hasDestination != null) {
            if (hasDestination) {
              return 'DESTINATION';
            }

            return 'END_SCREEN';
          }
          return null;

        case 'DESTINATION':
          if (destinationGeoId != null) {
            return 'DATES';
          }

          return 'END_SCREEN';

        // Show the trip questions in the order specified by the questionSteps array
        case 'DATES':
        case 'SEGMENT':
        case 'INTERESTS': {
          const index = questionSteps.indexOf(step);
          return questionSteps[index + 1] || 'END_SCREEN';
        }

        case 'END_SCREEN':
          return null;

        default:
          throw new Error(`Invalid step: ${(step: empty)}`);
      }
    },
    [questionSteps, page, mode],
  );

  // For the registration flow, we first ask for the user's name and hometown. Otherwise, we skip
  // right to the first trip question.
  const isMobile = useContext(Device).viewportCategory === 'MOBILE';
  const isOneTapWithProfile = mode === 'ONETAP' && !isMobile;
  let initialStep;
  if (mode === 'ONBOARDING' || isOneTapWithProfile) {
    initialStep = 'PROFILE';
  } else if (mode === 'ONETAP' && isMobile) {
    initialStep = 'HAS_DESTINATION';
  } else if (mode === 'GEOPILL') {
    initialStep = 'DESTINATION';
  } else {
    [initialStep] = questionSteps;
  }

  const initialResponses = useMemo(() => {
    const responses: Responses = {
      hasDestination:
        (mode !== 'ONBOARDING' && mode !== 'ONETAP' && scopedGeoId != null) || mode === 'GEOPILL'
          ? true
          : null,
      isInDestination: false,
      destinationGeoId: scopedGeoId, // default to the scoped geo, if any
      flexibleDates: false,
      startDate: null,
      endDate: null,
      startMonth: null,
      endMonth: null,
      segment: null,
      interests: [],
      firstName: '',
      homeGeoId: null,
    };
    Object.assign(responses, getResponses(initialExplicitPreferencesData));
    return responses;
  }, [mode, scopedGeoId, initialExplicitPreferencesData]);

  const state = useQuestionFlow<Step, Responses>({ initialStep, initialResponses, getNextStep });
  const { steps, currentStepIndex, nextStep, responses, setResponses, goForward } = state;

  /**
   * Geo-dependant personalization respones. When the user picks a new destination geo, we'll
   * fetch some of the data again from the service, since trip preferences questions may depend on
   * the geo.
   */
  const [{ data: explicitPreferencesData }] = useQuery({
    query: ExplicitPreferencesQuery,
    variables: { destinationGeoId: responses.destinationGeoId },
    pause,
  });

  // Any existing responses that have already been submitted to the service
  const existingResponses = useMemo(() => getResponses(explicitPreferencesData), [
    explicitPreferencesData,
  ]);

  useEffect(() => {
    setResponses(existingResponses, false);
  }, [setResponses, existingResponses]);

  const [hasSaved, setHasSaved] = useState(false);
  const hasExistingResponses = hasSaved || Object.keys(existingResponses).length > 0;

  const interestOptions: {| value: string, label: string |}[] = useMemo(() => {
    const labelsByInterestId = new Map();

    (explicitPreferencesData?.explicitPreferences?.interests?.value || []).forEach(interest => {
      labelsByInterestId.set(getInterestId(interest), interest?.display?.displayText || '');
    });

    setHasInterestOptions(labelsByInterestId.size > 0);

    return [...labelsByInterestId].map(([value, label]) => ({ value, label }));
  }, [explicitPreferencesData]);

  const currentStep = steps[currentStepIndex];

  /**
   * true if an answer has been selected for the current step. If this is false, the next button
   * does not appear, although the user can still skip the step if we know enough information to
   * pick what comes next.
   */
  const isStepComplete = useMemo(() => {
    switch (currentStep) {
      case 'PROFILE':
        // This is considered "complete" if either field is filled out
        return responses.firstName !== '' || responses.homeGeoId != null;

      case 'HAS_DESTINATION':
        return false;

      case 'DESTINATION':
        return responses.destinationGeoId != null;

      case 'DATES':
        if (responses.flexibleDates) {
          return responses.startMonth != null && responses.endMonth != null;
        }

        return responses.startDate != null && responses.endDate != null;

      case 'SEGMENT':
        return responses.segment != null;

      case 'INTERESTS':
        return responses.interests.length > 0;

      case 'END_SCREEN':
        return true;

      default:
        throw new Error(`Invalid step: ${(currentStep: empty)}`);
    }
  }, [currentStep, responses]);

  // Compute the number of questions answered so far and how many total questions there are
  const progress = useMemo(() => {
    if (questionSteps.includes(currentStep)) {
      return {
        stepNumber: questionSteps.indexOf(currentStep) + 1,
        stepCount: questionSteps.length,
      };
    }

    return null;
  }, [currentStep, questionSteps]);

  const [, mutateProfile] = useMutation(ProfileMutation);
  const [, mutateExplicitPreferences] = useMutation(ExplicitPreferencesMutation);

  /**
   * @return a promise that resolves when the responses have been submitted
   */
  const save = useCallback(() => {
    const mutations = [];

    // During the registration flow, set up the user's profile
    if ((mode === 'ONBOARDING' || mode === 'ONETAP') && userId != null) {
      mutations.push(
        mutateProfile({
          userId,
          homeGeoId: responses.homeGeoId,
          firstName: responses.firstName,
        }),
      );
    }

    // If the user has trip preferences, submit them to the Explicit Preferences service
    if (responses.hasDestination) {
      const startDate = responses.flexibleDates ? responses.startMonth : responses.startDate;
      const endDate = responses.flexibleDates ? responses.endMonth : responses.endDate;

      mutations.push(
        mutateExplicitPreferences({
          userId,
          destinationGeoId: responses.destinationGeoId,
          flexibleDates: responses.flexibleDates,
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
          segment: responses.segment,
          source: 'ONBOARDING',
          interests: responses.interests.map(parseInterestId),
        }),
      );
    }

    setHasSaved(true);

    return Promise.all(mutations);
  }, [userId, mode, responses, mutateProfile, mutateExplicitPreferences]);

  const [destinationGeoName, setDestinationGeo] = useLocationIdAndName(
    responses.destinationGeoId,
    initialDestinationGeoName,
    useCallback(
      (destinationGeoId: ?number) => goForward({ isInDestination: false, destinationGeoId }),
      [goForward],
    ),
  );

  const [homeGeoName, setHomeGeo] = useLocationIdAndName(
    responses.homeGeoId,
    initialHomeGeoName,
    useCallback((homeGeoId: ?number) => setResponses({ homeGeoId }), [setResponses]),
  );

  const triggerEmail = useCallback(() => {
    if ((mode === 'ONBOARDING' || mode === 'ONETAP') && userId != null) {
      fetch('/data/1.0/memberEngagement/onboarding/explicit-preferences', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          homeGeoId: responses.homeGeoId,
          firstName: responses.firstName,
          destinationGeoId: responses.destinationGeoId,
          segment: responses.segment,
        }),
      });
    }
  }, [mode, userId, responses]);

  return {
    ...state,
    progress,
    canGoBack: currentStep !== 'END_SCREEN',
    canGoForward: nextStep != null && isStepComplete && currentStep !== 'HAS_DESTINATION',
    canSkip: nextStep != null && !isStepComplete && currentStep !== 'HAS_DESTINATION',
    goForward,
    save,
    triggerEmail,
    destinationGeoName,
    homeGeoName,
    interestOptions,
    hasExistingResponses,
    setters: {
      setHasDestination: useCallback((hasDestination: ?boolean) => goForward({ hasDestination }), [
        goForward,
      ]),
      setIsInDestination: useCallback(
        () => goForward({ isInDestination: true, destinationGeoId: null }),
        [goForward],
      ),
      setDestinationGeo,
      setMonthGranularity: useCallback(
        (flexibleDates: boolean) => setResponses({ flexibleDates }),
        [setResponses],
      ),
      setFlexibleDates: useCallback((flexibleDates: boolean) => setResponses({ flexibleDates }), [
        setResponses,
      ]),
      setStartDate: useCallback((startDate: ?Date) => setResponses({ startDate }), [setResponses]),
      setEndDate: useCallback((endDate: ?Date) => setResponses({ endDate }), [setResponses]),
      setStartMonth: useCallback((startMonth: ?Date) => setResponses({ startMonth }), [
        setResponses,
      ]),
      setEndMonth: useCallback((endMonth: ?Date) => setResponses({ endMonth }), [setResponses]),
      setSegment: useCallback((segment: ?SegmentOption) => setResponses({ segment }), [
        setResponses,
      ]),
      setInterests: useCallback((interests: string[]) => setResponses({ interests }), [
        setResponses,
      ]),
      setFirstName: useCallback((firstName: string) => setResponses({ firstName }), [setResponses]),
      setHomeGeo,
    },
  };
};

export default useExplicitPreferencesState;

export type ExplicitPreferencesState = $Call<typeof useExplicitPreferencesState, *, *>;
export type { SegmentOption };
